/* eslint-disable */
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal, Loader } from 'ui-kit';
import { toast } from 'react-toastify';
import { notification } from 'utils/services';
import { BUTTON_TYPES, USER_ROLE_TYPES } from '_constants';
import { useDispatch, useSelector } from 'react-redux';
import { RetailerEntity, UserEntity, VisitEntity, AuthEntity } from '_entities';
import apiData from '_entities/old/api/config';
import * as Styled from '../styles';
import * as ModalStyles from './styles';

const { postRetailersImport, getRetailersImportProgress, resetRetailerImportData } = RetailerEntity.actions;
const { postUsersImport, getUsersImportProgress, resetUserImportInfo } = UserEntity.actions;
const { postVisitsImport } = VisitEntity.actions;
const { getLastRetailerImport, getRetailerToastId } = RetailerEntity.selectors;
const { getLastUserImport, getUserToastId } = UserEntity.selectors;
const { getProfileData } = AuthEntity.actions;
const { getUserRoleType } = AuthEntity.selectors;

const getFileDisplayName = ({ file, isDropDown }) => {
  if (!file && !isDropDown) return 'No file chosen';
  if (!file && isDropDown) return 'Drag & Drop your CSV file here or click to select';
  const { name } = file;
  return `File selected: ${name.length > 130 ? `${name.slice(0, 129)}...` : name}`;
};

const { hostname } = window.location;
const IsMdAccount = hostname?.split('.')[0] === 'md';

const PROGRESS_STATES = ['VALIDATION_FAILED', 'SUCCESS', 'FAILED'];

// MaM templates carry the extra "environment" column required by the P20M-1333
// upload gate; every other role gets the original columns without it.
const TEMPLATE_PATHS = {
  retailers: {
    default: IsMdAccount
      ? 'static/Template_Batch Retailer Upload_Use For POST Survey MD.csv'
      : 'static/Template_Batch Retailer Upload_Use For POST Survey.csv',
    mam: IsMdAccount
      ? 'static/Template_Batch Retailer Upload_Use For POST Survey MD MAM.csv'
      : 'static/Template_Batch Retailer Upload_Use For POST Survey MAM.csv',
  },
  users: {
    default: 'static/Template_Batch User Upload_Use For POST Survey.csv',
    mam: 'static/Template_Batch User Upload_Use For POST Survey MAM.csv',
  },
};

const MODAL_ACTIONS = {
  import: {
    retailers: postRetailersImport,
    users: postUsersImport,
    visits: postVisitsImport,
  },
  importProgress: {
    retailers: getRetailersImportProgress,
    users: getUsersImportProgress,
  },
  resetImportInfo: {
    retailers: resetRetailerImportData,
    users: resetUserImportInfo,
  },
  payloadType: {
    retailers: 'RetailersImportProcess',
    users: 'UsersImportProcess',
  },
  description: {
    retailers: 'Please refer to the instructions below to batch upload retailers to POST Survey.',
    users: 'Please refer to the instructions below to batch upload users to POST Survey.',
    visits: 'Please refer to the instructions below to batch upload visits to POST Survey.',
  },
  // retailers/users lists depend on the role: the environment paragraph only
  // makes sense for MaM, whose template has that column.
  instructionsList: {
    retailers: (isMam) => [
      <p>Use only the provided upload template. Do not modify column headers.</p>,
      <p>Required fields are denoted with *. Leave other fields blank if unknown or not applicable.</p>,
      <p>
        The lic_num, other1 and other2 fields are optional free text fields to input other identifying details about a
        retailer, such as license number, corporation name, etc.
      </p>,
      <p>Upload one file at a time, in .csv format.</p>,
      isMam && (
        <p>
          Fill the "environment" column in every row with the environment this file is intended for: P2Dev, P2Tst,
          P2Stg, P2Prd or P2Uat (case does not matter). The whole upload is rejected if any row does not match the
          environment you are uploading to.
        </p>
      ),
      <p>Before uploading, double-check that all fields are completed correctly.</p>,
      <p>The progress bar shows the status of your upload. Once the upload begins, it cannot be interrupted.</p>,
      <p>Retailers are successfully uploaded upon seeing a confirmation message.</p>,
      <p>
        If the upload fails, you will receive an email from <span>noreply@countertools.org</span> with details on what
        to fix. Check your Spam folder if you don't see it.
      </p>,
    ],
    users: (isMam) => [
      <p>Use only the provided upload template. Do not modify column headers.</p>,
      <p>Required fields are denoted with *. Leave other fields blank if unknown or not applicable.</p>,
      <p>Enter "PS Data Collector" as the role for all users.</p>,
      <p>
        Enter the applicable county name in the geog1 field. If a user needs to be assigned to additional counties, use
        the geog2-geog_other fields.
      </p>,
      <p>Enter the agency name listed in the site for the agency field.</p>,
      <p>Upload one file at a time, in .csv format.</p>,
      isMam && (
        <p>
          Fill the "environment" column in every row with the environment this file is intended for: P2Dev, P2Tst,
          P2Stg, P2Prd or P2Uat (case does not matter). The whole upload is rejected if any row does not match the
          environment you are uploading to.
        </p>
      ),
      <p>Before uploading, double-check that all fields are completed correctly.</p>,
      <p>The progress bar shows the status of your upload. Once the upload begins, it cannot be interrupted.</p>,
      <p>
        Users are successfully uploaded upon seeing a confirmation message. These users will automatically receive an
        email ("Set your password") from <span>noreply@countertools.org</span> to make a password and log in.
      </p>,
      <p>
        If the upload fails, you will receive an email from <span>noreply@countertools.org</span> with details on what
        to fix. Check your Spam folder if you don't see it.
      </p>,
    ],
    // Visits have no upload template: the CSV columns depend on the account's
    // visit form (dynamic form fields), so no universal template can exist.
    // Like retailers/users, only MaM uploads are environment-gated, so the
    // environment paragraph is role-conditional here too.
    visits: (isMam) => [
      <p>
        The CSV columns must match your account's visit form. Please see documentation for complete details on the
        required format.
      </p>,
      <p>
        Required fields: visit_type, visit_time, agency, form, retail_name, retail_address, retail_city, retail_state,
        retail_zipcode_orig.
      </p>,
      isMam && (
        <p>
          Include an "environment" column naming the environment this file is intended for: P2Dev, P2Tst, P2Stg, P2Prd
          or P2Uat (case does not matter). The whole upload is rejected if any row does not match the environment you
          are uploading to.
        </p>
      ),
      <p>Upload one file at a time, in .csv format.</p>,
      <p>Before uploading, double-check that all fields are completed correctly.</p>,
      <p>
        <b>Please ensure you are uploading to the correct account; this cannot be changed later.</b>
      </p>,
      <p>
        The file is processed in the background. Processing may take up to several hours, depending on the size of the
        file.
      </p>,
      <p>
        You will receive an email from <span>noreply@countertools.org</span> with the results of the upload. Check your
        Spam folder if you don't see it.
      </p>,
    ],
  },
};

const { baseUrl } = apiData;

function BatchUploadModal({ isOpen, onClose, modalType }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [base64, setBase64] = useState('');
  const lastRetailerImport = useSelector(getLastRetailerImport);
  const lastUserImport = useSelector(getLastUserImport);
  const retailerToastId = useSelector(getRetailerToastId);
  const userToastId = useSelector(getUserToastId);
  const isMam = useSelector(getUserRoleType) === USER_ROLE_TYPES.MULTI_ACCOUNT_MANAGER;
  const templatePath = TEMPLATE_PATHS[modalType]?.[isMam ? 'mam' : 'default'];
  const dispatch = useDispatch();
  const [isTemplateButtonDisabled, setIsTemplateButtonDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const LAST_IMPORT = {
    lastImport: { users: lastUserImport, retailers: lastRetailerImport },
    resetImportInfo: { users: resetUserImportInfo, retailers: resetRetailerImportData },
  };

  const handleDownloadTemplateBtn = () => {
    setIsLoading(true);
    setIsTemplateButtonDisabled(true);
    try {
      window.location.href = `${baseUrl}${templatePath}`;
    } catch (e) {
      console.log(e);
    } finally {
      setIsTemplateButtonDisabled(false);
      setIsLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'text/csv') {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBase64(reader.result);
      };
      reader.readAsDataURL(droppedFile);
      setFile(droppedFile);
    } else {
      notification.error('Please drop a CSV file.');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleFileInput = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBase64(reader.result);
      };
      reader.readAsDataURL(selectedFile);
      setFile(selectedFile);
    } else {
      notification.error('Please select a CSV file.');
    }
  };

  const openFileDialog = () => {
    fileInputRef.current.click();
  };

  const handleUploadFile = async () => {
    if (!file) return notification.error('You should select a CSV file first.');
    if (PROGRESS_STATES.includes(LAST_IMPORT.lastImport[modalType]?.attributes?.status)) {
      await dispatch(LAST_IMPORT.resetImportInfo[modalType]());
      toast.dismiss(modalType === 'users' ? userToastId : retailerToastId);
    }
    setIsLoading(true);
    try {
      // Types without an ImportProcess model (visits) post the raw file as
      // multipart form-data; the backend emails the results instead of
      // exposing an import-progress resource.
      if (!MODAL_ACTIONS.payloadType[modalType]) {
        const formData = new FormData();
        formData.append('file', file);
        await dispatch(MODAL_ACTIONS.import[modalType](formData));
        notification.success('File submitted. Check your email for the results of the upload.');
      } else {
        const payload = {
          data: {
            type: MODAL_ACTIONS.payloadType[modalType],
            attributes: {
              import_file: {
                filename: file?.name,
                content: base64,
              },
            },
          },
        };

        await dispatch(MODAL_ACTIONS.import[modalType](payload));
        await dispatch(getProfileData());
      }
    } catch (err) {
      const errorMessageArray = err?.response?.data?.errors;

      if (Array.isArray(errorMessageArray)) {
        const [errorMessage] = errorMessageArray;
        notification.error(errorMessage.detail);
      } else {
        notification.error('Something went wrong during the request');
      }
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  if (isLoading) return <Loader />;

  return (
    <Modal open={isOpen} onClose={isLoading ? () => {} : onClose}>
      <ModalStyles.ModalWrapper>
        <Styled.ModalTitle>Batch upload {modalType}</Styled.ModalTitle>
        <ModalStyles.ModalDescription>{MODAL_ACTIONS.description[modalType]}</ModalStyles.ModalDescription>
        {templatePath && (
          <ModalStyles.ButtonWrapper>
            <Styled.ModalButtonSet
              onClick={handleDownloadTemplateBtn}
              text="Download template"
              variant={BUTTON_TYPES.DANGER}
              aria-label="Download template"
              disabled={isTemplateButtonDisabled}
            />
          </ModalStyles.ButtonWrapper>
        )}

        <ModalStyles.List>
          {MODAL_ACTIONS.instructionsList[modalType]?.(isMam)
            .filter(Boolean)
            .map((el, index) => (
              <li key={index}>{el}</li>
            ))}
        </ModalStyles.List>

        <ModalStyles.DropZone onDrop={handleDrop} onDragOver={handleDragOver} onClick={openFileDialog}>
          <ModalStyles.ChosenFileText>{getFileDisplayName({ file, isDropDown: true })}</ModalStyles.ChosenFileText>
        </ModalStyles.DropZone>
        <ModalStyles.FileInput type="file" accept=".csv" ref={fileInputRef} onChange={handleFileInput} />
        <ModalStyles.FilePickerRow>
          <ModalStyles.ChooseFileButton onClick={openFileDialog}>Choose File</ModalStyles.ChooseFileButton>
          <ModalStyles.ChosenFileText>{getFileDisplayName({ file })}</ModalStyles.ChosenFileText>
        </ModalStyles.FilePickerRow>
        <Styled.ModalButtonSet
          onClick={handleUploadFile}
          text="Upload .csv file"
          variant={BUTTON_TYPES.DARK}
          aria-label="Upload .csv file"
        />
      </ModalStyles.ModalWrapper>
    </Modal>
  );
}

BatchUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  modalType: PropTypes.string,
};

BatchUploadModal.defaultProps = {
  modalType: 'retailers',
};

export default BatchUploadModal;
